import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import qs from 'query-string';

import getEnvConfig from 'utils/config/env';

import { PaginatedResponse } from '../../../../communities/interfaces/PaginatedResponse';
import { UsersAdminProps, UsersRequest } from './users.inteface';
import { authHeader } from '../../../../../utils/common/authHeader';

export const adminUsersApi = createApi({
    reducerPath: 'adminUsersApi',
    baseQuery: fetchBaseQuery({
        baseUrl: getEnvConfig().env.API_URL + '/manager',
        prepareHeaders: (headers: Headers) => authHeader(headers),
    }),
    tagTypes: ['adminUsers'],
    endpoints: build => ({
        usersList: build.query<
            PaginatedResponse<UsersAdminProps>,
            UsersRequest
        >({
            query: payload => {
                const queryArgs = qs.stringify(payload.filters, {
                    arrayFormat: 'bracket-separator',
                    skipNull: true,
                });
                return {
                    url: `users?${queryArgs}`,
                };
            },
            transformResponse: (res: { data: UsersAdminProps[] }, meta) => {
                return {
                    items: res.data || [],
                    total:
                        Number(meta.response.headers.get('items-count')) || 0,
                };
            },
            providesTags: ['adminUsers'],
        }),
        getUserById: build.query({
            query: (id: number) => ({
                url: `users/${id}`,
            }),
        }),
        blockUserById: build.mutation({
            query: (id: number) => ({
                method: 'GET',
                url: `users/block/${id}`,
            }),
            invalidatesTags: ['adminUsers'],
        }),
        userToAdmin: build.mutation({
            query: id => ({
                method: 'GET',
                url: `users/make-admin/${id}`,
            }),
        }),
        adminToUser: build.mutation({
            query: id => ({
                method: 'GET',
                url: `users/revoke-admin/${id}`,
            }),
        }),
        sendNewPassword: build.mutation({
            query: id => ({
                method: 'GET',
                url: `users/send-new-password/${id}`,
            }),
        }),
        unblockUserById: build.mutation({
            query: id => ({
                method: 'GET',
                url: `users/unblock/${id}`,
            }),
            invalidatesTags: ['adminUsers'],
        }),
        updateCommission: build.mutation({
            query: ({ id, commission }) => ({
                method: 'PUT',
                url: `users/${id}`,
                body: {
                    commission: commission,
                },
            }),
            invalidatesTags: ['adminUsers'],
        }),

        readUserToken: build.mutation({
            query: id => ({
                method: 'POST',
                url: `users/login-as`,
                body: {
                    id: id,
                },
            }),
            transformResponse: (res: any) => {
                return res.data || '';
            },
        }),
    }),
});

export const {
    useBlockUserByIdMutation,
    useSendNewPasswordMutation,
    useUnblockUserByIdMutation,
    useLazyUsersListQuery,
    useUpdateCommissionMutation,
    useReadUserTokenMutation,
} = adminUsersApi;
